import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";
import icons from "../icons";
import GrIconButton from "./GrIconButton";
import GrIcon from "./GrIcon";

// Page title block: eyebrow (breadcrumb/date), title, subtitle, actions on the right.
// Title 24/500 (20 on phones) and subtitle 14, as in most SaaS apps. The space above it comes from
// GrPage (the page frame), so every page starts at the same height.
// No `title` (e.g. a post page that only needs the back arrow) renders no empty <h1>.
// `onBack` adds the page's back arrow before the title (one look on every sub-page); `backLabel` is its
// screen-reader text. `leading` is for anything else in that place.
// `trailing` (e.g. a status pill) sits after the h1 on the title line and wraps under it when space runs out.
// Uses a real <h1>. The font-size carries !important only to beat the legacy global rule
// `h1 { font-size: 33px !important }` in src/assets/css/new.css.
export default function GrPageHeader({
  eyebrow,
  onBack,
  backLabel = "Back",
  leading,
  title,
  trailing,
  subtitle,
  actions,
  sx,
}) {
  // The back button and the title line share one row height (a button's: 40, 44 on touch), so the
  // arrow, the title and a button beside it (trailing) all centre on one line, and the subtitle starts
  // under the title text (not under the back button).
  const rowHeight = {
    minHeight: gr.control.md,
    "@media (pointer: coarse), (max-width: 899.95px)": {
      minHeight: gr.control.touch,
    },
  };
  const lead = onBack ? (
    <GrIconButton
      variant="ghost"
      aria-label={backLabel}
      onClick={onBack}
      sx={{
        // Pull the button left by its inner padding so the arrow glyph sits on the content edge.
        ml: `calc((${gr.icon.lg} - ${gr.control.md}) / 2)`,
        color: gr.color.brand,
      }}
    >
      <GrIcon icon={icons.arrowBack} />
    </GrIconButton>
  ) : (
    leading
  );
  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        // Title-only headers centre the action on the title line; with a subtitle the action sits at the bottom.
        alignItems: subtitle ? "flex-end" : "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: gr.space[4],
        mb: gr.space[6],
        ...sx,
      }}
    >
      {/* 180px basis: on phones a short action (e.g. "Friends") stays on the title line instead of wrapping. */}
      <Box sx={{ minWidth: 0, flex: "1 1 180px" }}>
        {eyebrow ? (
          <Box
            sx={{
              fontSize: gr.font.size.sm,
              color: gr.color.muted,
              mb: gr.space[1],
            }}
          >
            {eyebrow}
          </Box>
        ) : null}
        {/* `leading` (e.g. a back button) sits beside the h1, not inside it, so the heading text stays clean. */}
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: gr.space[1] }}
        >
          {lead ? (
            <Box
              sx={{
                flex: "none",
                display: "flex",
                alignItems: "center",
                ...rowHeight,
              }}
            >
              {lead}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: trailing ? "wrap" : "nowrap",
                columnGap: gr.space[4],
                rowGap: gr.space[1],
                // Always a button's height, with or without a button beside it, so the title sits at the
                // same height on every page.
                ...rowHeight,
              }}
            >
              {title ? (
                <Box
                  component="h1"
                  sx={{
                    m: 0,
                    minWidth: 0,
                    fontFamily: gr.font.family,
                    fontSize: `${gr.font.size.h2} !important`,
                    fontWeight: gr.font.weight.medium,
                    lineHeight: gr.font.lineHeight.tight,
                    letterSpacing: "-0.01em",
                    color: gr.color.brandStrong, // text-safe teal (AA)
                    overflowWrap: "anywhere",
                    "@media (max-width: 599.95px)": {
                      fontSize: `${gr.font.size.xxl} !important`,
                    },
                  }}
                >
                  {title}
                </Box>
              ) : null}
              {trailing}
            </Box>
            {subtitle ? (
              <Box
                // A plain line is a <p>; richer content (e.g. a description with files) is a <div>.
                component={typeof subtitle === "string" ? "p" : "div"}
                sx={{
                  m: 0,
                  mt: gr.space[1],
                  fontSize: gr.font.size.md,
                  color: gr.color.ink2,
                  fontWeight: gr.font.weight.regular,
                }}
              >
                {subtitle}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
      {actions ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: gr.space[3],
            alignItems: "center",
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}

GrPageHeader.propTypes = {
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  leading: PropTypes.node,
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  trailing: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  sx: PropTypes.object,
};
